import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth, formatApiError } from "../auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { UPDATES, formatUpdateDate } from "../data/updates";

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_brand-analytics-10/artifacts/1ogynudb_logo_horizontal_HT.png";
const LOGO_HORIZONTAL_WHITE = "https://customer-assets.emergentagent.com/job_brand-analytics-10/artifacts/f3z2oza4_TZ_RGB_d_HorAlt.png";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_brand-analytics-10/artifacts/mmekj5gq_logo_tzanetatos_.png";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/modules" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Bienvenido de vuelta");
      navigate("/modules");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white" data-testid="login-page">
      {/* LEFT: hero */}
      <div className="relative hidden lg:block overflow-hidden bg-slate-950">
        <img
          src="https://customer-assets.emergentagent.com/job_brand-analytics-10/artifacts/fp86k8jw_portada_.png"
          alt="Tzanetatos"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950/80 via-emerald-900/30 to-transparent" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <img src={LOGO_HORIZONTAL_WHITE} alt="Tzanetatos" className="h-14 object-contain" />
          </div>
          <div>
            <h1 className="font-display text-5xl xl:text-6xl leading-[0.95] font-bold tracking-tight">
              Descubre<br />una nueva<br />
              <span className="text-emerald-400">herramienta.</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              Reportes de visita en campo, fotos en la nube y exportación a Excel — desde cualquier dispositivo.
            </p>
          </div>
          <div />
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="flex items-center justify-center p-6 md:p-12 tv-grid-bg">
        <div className="w-full max-w-md page-fade-in">
          <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <img src={LOGO_HORIZONTAL} alt="Tzanetatos" className="h-12 object-contain" />
          </div>

          <div className="mb-10">
            <div className="kbd-label mb-3 text-emerald-700">/ ACCESO SEGURO</div>
            <h2 className="font-display text-4xl font-bold tracking-tight text-slate-950">
              Inicia sesión
            </h2>
            <p className="mt-2 text-slate-500">Usa tus credenciales asignadas por el administrador.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="kbd-label">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                className="mt-2 h-12 rounded-md border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                required
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="kbd-label">Contraseña</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="mt-2 h-12 rounded-md border-slate-300 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-emerald-700 hover:bg-emerald-800 text-white rounded-md font-medium text-base group"
              data-testid="login-submit-btn"
            >
              {loading ? "Ingresando…" : "Ingresar"}
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-slate-200 flex items-center gap-2 text-xs text-slate-500">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            <span>Acceso protegido. ¿Olvidaste tu contraseña? Contacta al administrador.</span>
          </div>

          {/* Últimas actualizaciones — sólo fechas + etiquetas genéricas */}
          <div className="mt-8" data-testid="updates-section">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-3.5 h-3.5 text-emerald-700" />
              <div className="kbd-label text-emerald-700">/ ÚLTIMAS ACTUALIZACIONES</div>
            </div>
            <ul className="space-y-2.5" data-testid="updates-list">
              {UPDATES.slice(0, 4).map((u) => (
                <li key={u.date} className="flex items-center gap-3 text-sm" data-testid={`update-${u.date}`}>
                  <span className="inline-flex items-center justify-center w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
                  <span className="font-mono-tv text-[11px] uppercase tracking-widest text-slate-500 tabular-nums w-24 shrink-0">
                    {formatUpdateDate(u.date)}
                  </span>
                  <span className="text-slate-700 truncate">{u.label}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[11px] text-slate-400 leading-relaxed">
              La plataforma se mantiene en mejora continua. Fechas de las actualizaciones más recientes.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
